import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import {
  useIsAuthenticated,
  useMsal,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useAccount,
} from "@azure/msal-react";
import {
  AccountInfo,
  AuthError,
  IPublicClientApplication,
} from "@azure/msal-browser";

import { NestedRoutes } from "./routes.types";
import { ConditionalWrapper } from "../pages/ConditionalWrapper";
import { intersect, ProtectedRoute } from "../components/ProtectedRoute";
import { loginRequest } from "../authConfig";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import Repository from "../services/api/Repository";
import {
  setCurrentUser,
  setToken,
  setUserProfilePicture,
} from "../store/slices/AuthSlice";
import { LoginLoading } from "../pages/Auth/LoginLoading";
import { Page } from "./App.styles";
import { NavigationBar } from "../pages/Auth/NavigationBar";
import { Footer } from "@milestone/component-library";
import { adminRoutes } from "../pages/Admin/routes";
import { dashboardRoutes } from "../pages/Dashboard/routes";
import { Logout } from "../pages/Auth/Logout";
import { Login } from "../pages/Auth/Login";
import { Api } from "../services/api";
import { UserPermissions } from "../enums";
import { ComingSoon } from "../pages/Auth/ComingSoon";
import { metricsRoutes } from "../pages/Metrics/routes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

export const LoginError = "Login Error" as const;

const placeRoute = (
  path: string,
  route: NestedRoutes,
  siblings: Array<NestedRoutes>
) => {
  if (!route.permissions) {
    return (
      <Route exact key={route.path} path={path} component={route.component} />
    );
  } else {
    return (
      <ProtectedRoute
        exact
        key={route.path}
        path={path}
        children={route.component}
        permissions={route.permissions}
        siblings={siblings}
      />
    );
  }
};

const mapChildren = (path: string, children: Array<NestedRoutes>) => {
  return children.map(route => {
    if (route.path !== undefined) {
      if (route.children) {
        return createRoutes(route, path, children);
      }
      return placeRoute(`${path}/${route.path}`, route, children);
    } else return null;
  });
};

const createRoutes = (
  routes: NestedRoutes,
  path = "",
  siblings: Array<NestedRoutes>
) => {
  path += `/${routes.path}`;
  return (
    <Route key={routes.path} path={path}>
      <ConditionalWrapper
        condition={!!routes.layout}
        wrapper={routes.layout}
        extraProps={{ path: path + "/", links: routes.children }}
      >
        {placeRoute(path, routes, siblings)}
        <Switch>
          {routes.children && mapChildren(path, routes.children)}
          {routes.defaultPath && (
            <Redirect to={`${path}/${routes.defaultPath}`} />
          )}
        </Switch>
      </ConditionalWrapper>
    </Route>
  );
};

export const ErrorComponent: React.FC<{ error: AuthError | null }> = ({
  error,
}) => {
  return <h6>An Error Occurred: {error?.errorCode}</h6>;
};
export const loginUser = async (
  dispatch: ReturnType<typeof useAppDispatch>,
  instance: IPublicClientApplication,
  account: AccountInfo | null,
  setBackendLoginInProgress?: Dispatch<SetStateAction<boolean>>
) => {
  if (setBackendLoginInProgress) setBackendLoginInProgress(true);
  try {
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account: account ?? undefined,
    });
    Repository.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${tokenResponse.accessToken}`;

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/auth/login`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken: tokenResponse.accessToken,
        }),
      }
    );
    const userResponse = await response.json();

    dispatch(setToken(tokenResponse.accessToken));
    dispatch(setCurrentUser(userResponse));

    if (response.status !== 200) throw Error(LoginError);
    const picture = await Api.members.getMemberProfilePicture(userResponse.id);
    dispatch(setUserProfilePicture(picture));

    if (setBackendLoginInProgress) setBackendLoginInProgress(false);
  } catch (error) {
    if (error instanceof Error) {
      if (error.message === LoginError) {
        console.error(error);
        localStorage.setItem(LoginError, LoginError);
        if (setBackendLoginInProgress) setBackendLoginInProgress(false);
        instance.logoutRedirect({
          onRedirectNavigate: () => {
            return false;
          },
        });
      } else {
        instance.loginRedirect();
      }
    } else instance.loginRedirect();
  }
};

const App = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const user = useAppSelector(state => state.auth.user);
  const [backendLoginInProgress, setBackendLoginInProgress] = useState(false);
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  useEffect(() => {
    if (
      isAuthenticated &&
      (typeof user === "undefined" ||
        user === null ||
        typeof user.permissions === "undefined")
    ) {
      if (!backendLoginInProgress) {
        loginUser(dispatch, instance, account, setBackendLoginInProgress);
      }
    }
  });

  return (
    <>
      {backendLoginInProgress && <LoginLoading />}
      <AuthenticatedTemplate>
        {typeof user !== "undefined" &&
          user !== null &&
          typeof user.permissions !== "undefined" &&
          !backendLoginInProgress && (
            <QueryClientProvider client={queryClient}>
              <Page>
                <NavigationBar
                  routes={{
                    children: [adminRoutes, dashboardRoutes, metricsRoutes],
                  }}
                />
                <Switch>
                  {createRoutes(dashboardRoutes, "", [])}
                  {createRoutes(adminRoutes, "", [])}
                  {createRoutes(metricsRoutes, "", [])}
                  <Route path="/logout">
                    <Logout />
                  </Route>
                  <Route path="/comingSoon">
                    <ComingSoon />
                  </Route>
                  {intersect(user.permissions, [
                    UserPermissions.DashboardView,
                    UserPermissions.DashboardViewLead,
                  ]).length ? (
                    <Redirect to="/dashboard" />
                  ) : intersect(user.permissions, [
                      UserPermissions.MemberViewList,
                      UserPermissions.TeamViewList,
                      UserPermissions.RoleViewList,
                      UserPermissions.CompanyViewList,
                      UserPermissions.OfficeViewList,
                    ]).length ? (
                    <Redirect to="/admin" />
                  ) : (
                    <Redirect to="/comingSoon" />
                  )}
                </Switch>
                <Footer />
              </Page>
            </QueryClientProvider>
          )}
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <Route path="/login">
          <Login />
        </Route>
        <Redirect to="/login" />
      </UnauthenticatedTemplate>
    </>
  );
};

export default App;
